/**
 * ChampionBadgePanel — edits the pink starburst floating badge (top-right of
 * the hero). Mirrors EVERY field used by Home.jsx so the admin is a true
 * source of truth. Full 3-line text, per-line rotation, spike geometry,
 * shadows, inner-shadow, border, position anchors, effect selector, icon
 * picker and live SVG preview.
 */
import React from "react";
import { Sparkles, Trophy } from "lucide-react";
import { Label } from "@/components/ui/label";
import { setPath } from "./setPath.js";
import { ColorField } from "./fields.jsx";
import { BADGE_ICONS, MultiLangText } from "./shared.jsx";

export function ChampionBadgePanel({ cfg, setCfg }) {
  const b = cfg.champion_badge || {};
  const set = (k, v) => setCfg(setPath(cfg, ["champion_badge", k], v));
  const enabled = b.enabled !== false;
  const size = b.size_px || 220;

  // ── Live preview (mirror of Home.jsx ChampionBadge) ────────────────────
  const fillColor = b.bg_color || "#FF1A8C";
  const pts = Math.max(8, Math.min(60, b.spikes ?? 40));
  const rOut = 50;
  const rIn  = Math.max(20, Math.min(48, 50 - (b.spike_depth ?? 16)));
  const path = Array.from({ length: pts * 2 }, (_, i) => {
    const a = (Math.PI * i) / pts;
    const r = i % 2 === 0 ? rOut : rIn;
    return [50 + r * Math.cos(a), 50 + r * Math.sin(a)];
  }).map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`).join(" ") + " Z";
  const cornerRound = Math.max(0, Math.min(15, b.corner_round ?? 0));
  const borderColor = b.border_color || "#0A0E2A";
  const borderWidth = Math.max(0, Math.min(8, b.border_width ?? 0));
  const shX = b.shadow_x ?? 0;
  const shY = b.shadow_y ?? 10;
  const shBlur = b.shadow_blur ?? 40;
  const shAlpha = Math.max(0, Math.min(100, b.shadow_opacity ?? 60)) / 100;
  const hexToRgba = (hex, a) => {
    const h = String(hex || "#000").replace("#", "");
    const n = h.length === 3 ? h.split("").map(c => c + c).join("") : h;
    const r = parseInt(n.slice(0, 2), 16), g = parseInt(n.slice(2, 4), 16), bl = parseInt(n.slice(4, 6), 16);
    return `rgba(${r},${g},${bl},${a})`;
  };
  const shadowCss = `drop-shadow(${shX}px ${shY}px ${shBlur}px ${hexToRgba(b.shadow_color || "#FF1A8C", shAlpha)})`;
  const innerAlpha = Math.max(0, Math.min(100, b.inner_shadow_opacity ?? 0)) / 100;
  const innerOn = innerAlpha > 0;
  const innerX = b.inner_shadow_x ?? 0;
  const innerY = b.inner_shadow_y ?? 2;
  const innerBlur = b.inner_shadow_blur ?? 2;
  const innerColor = b.inner_shadow_color || "#000000";
  const innerId = `cb-admin-inner-${pts}-${rIn}`;
  const fontFam     = b.font_family || "Fredoka";
  const rot  = b.rotation_deg ?? -10;
  const r1   = b.line1_rotation ?? -6;
  const r2   = b.line2_rotation ?? -2;
  const r3   = b.line3_rotation ??  6;
  const previewSize = Math.min(size, 180);

  return (
    <div className="mt-5 border-t border-slate-100 pt-4" data-testid="champion-badge-panel">
      <div className="flex items-center justify-between mb-3">
        <Label className="text-sm font-bold text-slate-700 flex items-center gap-1.5">
          <Sparkles className="w-4 h-4 text-pink-500" /> Crachá do campeão (estrela rosa)
        </Label>
        <label className="text-xs font-bold text-slate-600 flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => set("enabled", e.target.checked)}
            data-testid="champion-enabled"
          />
          {enabled ? "Visível" : "Escondido"}
        </label>
      </div>

      <div className="grid md:grid-cols-[1fr_auto] gap-5">
        {/* ── Left: textual fields ── */}
        <div className="space-y-3">
          <MultiLangText
            label="Linha 1 (pequena, branca)"
            testidPrefix="champion-line1"
            value={{ pt: b.line1_pt, en: b.line1_en, es: b.line1_es }}
            onChange={(code, v) => set(`line1_${code}`, v)}
            placeholder="TORNA-TE"
          />
          <MultiLangText
            label="Linha 2 (média, branca)"
            testidPrefix="champion-line2"
            value={{ pt: b.line2_pt, en: b.line2_en, es: b.line2_es }}
            onChange={(code, v) => set(`line2_${code}`, v)}
            placeholder="O CAMPEÃO"
          />
          <MultiLangText
            label="Linha 3 (grande, amarela)"
            testidPrefix="champion-line3"
            value={{ pt: b.line3_pt, en: b.line3_en, es: b.line3_es }}
            onChange={(code, v) => set(`line3_${code}`, v)}
            placeholder="SUPERTMATIK!"
          />

          <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100">
            <ColorField label="Cor do fundo (rosa)" value={fillColor} onChange={(v) => set("bg_color", v)} testid="champion-bg" />
            <ColorField label="Cor das linhas 1+2 (branco)" value={b.text_color || "#FFFFFF"} onChange={(v) => set("text_color", v)} testid="champion-text" />
            <ColorField label="Cor do troféu + linha 3 (amarelo)" value={b.icon_color || "#FFD93D"} onChange={(v) => set("icon_color", v)} testid="champion-icon" />
            <div>
              <Label className="text-xs text-slate-600">Tamanho ({size}px)</Label>
              <input
                type="range" min={120} max={320}
                value={size}
                onChange={(e) => set("size_px", Number(e.target.value))}
                className="w-full mt-1"
                data-testid="champion-size"
              />
            </div>
          </div>

          {/* Effect + rotation controls */}
          <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100">
            <div>
              <Label className="text-xs text-slate-600">Tipo de letra</Label>
              <select
                value={fontFam}
                onChange={(e) => set("font_family", e.target.value)}
                className="mt-1 w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
                style={{ fontFamily: `"${fontFam}", system-ui, sans-serif` }}
                data-testid="champion-font"
              >
                <option value="Fredoka" style={{ fontFamily: "Fredoka" }}>Fredoka (default — chunky redondo)</option>
                <option value="Nunito" style={{ fontFamily: "Nunito" }}>Nunito (limpo)</option>
                <option value="Outfit" style={{ fontFamily: "Outfit" }}>Outfit (geométrico)</option>
                <option value="Bangers" style={{ fontFamily: "Bangers" }}>Bangers (comic book)</option>
                <option value="Bowlby One SC" style={{ fontFamily: "Bowlby One SC" }}>Bowlby One SC (vintage)</option>
                <option value="Luckiest Guy" style={{ fontFamily: "Luckiest Guy" }}>Luckiest Guy (cartoon)</option>
                <option value="Permanent Marker" style={{ fontFamily: "Permanent Marker" }}>Permanent Marker (handwritten)</option>
                <option value="Bungee" style={{ fontFamily: "Bungee" }}>Bungee (street sign)</option>
                <option value="Bungee Inline" style={{ fontFamily: "Bungee Inline" }}>Bungee Inline (outline)</option>
                <option value="Russo One" style={{ fontFamily: "Russo One" }}>Russo One (sports)</option>
                <option value="Black Ops One" style={{ fontFamily: "Black Ops One" }}>Black Ops One (militar)</option>
                <option value="Inter" style={{ fontFamily: "Inter" }}>Inter (minimal)</option>
              </select>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Efeito visual</Label>
              <select
                value={b.effect || "shimmer"}
                onChange={(e) => set("effect", e.target.value)}
                className="mt-1 w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
                data-testid="champion-effect"
              >
                <optgroup label="Sem animação">
                  <option value="none">Sem efeito</option>
                </optgroup>
                <optgroup label="Brilhos / luz">
                  <option value="shimmer">Shimmer (brilho a girar + estrelas)</option>
                  <option value="shimmer-sweep">Shimmer Sweep (barra diagonal a deslizar)</option>
                  <option value="shimmer-radial">Shimmer Radial (pulsação central)</option>
                  <option value="shimmer-holo">Shimmer Holo (arco-íris holográfico)</option>
                  <option value="shimmer-twinkle">Shimmer Twinkle (estrelas a cintilar)</option>
                  <option value="shimmer-lightning">Shimmer Lightning (flashes rápidos)</option>
                  <option value="flash">Flash (brilho súbito)</option>
                  <option value="flicker">Flicker (cintilar tipo neon)</option>
                  <option value="rainbow">Rainbow (cores a mudar)</option>
                </optgroup>
                <optgroup label="Escala / pulsação">
                  <option value="pulse">Pulse (bater lento)</option>
                  <option value="heartbeat">Heartbeat (coração)</option>
                  <option value="jump">Jump (salto periódico)</option>
                  <option value="tada">Tadá (apresentação)</option>
                </optgroup>
                <optgroup label="Movimento / rotação">
                  <option value="spin">Spin (gira lento)</option>
                  <option value="wobble">Wobble (abana)</option>
                  <option value="swing">Swing (pendular)</option>
                  <option value="bounce">Bounce (salta vertical)</option>
                </optgroup>
              </select>
            </div>
          </div>

          {/* Star geometry — spike count / depth / rounding */}
          <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100">
            <div className="col-span-2 text-[11px] font-bold uppercase tracking-wide text-slate-500 mt-1">Geometria da estrela</div>
            <div>
              <Label className="text-xs text-slate-600">Nº de bicos ({pts})</Label>
              <input
                type="range" min={8} max={60} step={1}
                value={b.spikes ?? 40}
                onChange={(e) => set("spikes", Number(e.target.value))}
                className="w-full mt-1"
                data-testid="champion-spikes"
              />
              <p className="text-[10px] text-slate-400">8 = poucos bicos largos · 60 = serra fina</p>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Profundidade dos bicos ({b.spike_depth ?? 16})</Label>
              <input
                type="range" min={0} max={30} step={1}
                value={b.spike_depth ?? 16}
                onChange={(e) => set("spike_depth", Number(e.target.value))}
                className="w-full mt-1"
                data-testid="champion-depth"
              />
              <p className="text-[10px] text-slate-400">0 = círculo · 30 = bicos muito afiados</p>
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-slate-600">Arredondamento dos bicos ({cornerRound})</Label>
              <input
                type="range" min={0} max={15} step={1}
                value={cornerRound}
                onChange={(e) => set("corner_round", Number(e.target.value))}
                className="w-full mt-1"
                data-testid="champion-round"
              />
              <p className="text-[10px] text-slate-400">0 = pontas afiadas · 15 = pontas arredondadas suaves</p>
            </div>
          </div>

          {/* Border / outline */}
          <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100">
            <div className="col-span-2 text-[11px] font-bold uppercase tracking-wide text-slate-500 mt-1">Rebordo</div>
            <ColorField label="Cor do rebordo" value={borderColor} onChange={(v) => set("border_color", v)} testid="champion-border-color" />
            <div>
              <Label className="text-xs text-slate-600">Espessura do rebordo ({borderWidth})</Label>
              <input
                type="range" min={0} max={8} step={1}
                value={borderWidth}
                onChange={(e) => set("border_width", Number(e.target.value))}
                className="w-full mt-1"
                data-testid="champion-border-width"
              />
              <p className="text-[10px] text-slate-400">0 = sem rebordo</p>
            </div>
          </div>

          {/* Shadow / drop-shadow */}
          <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100">
            <div className="col-span-2 text-[11px] font-bold uppercase tracking-wide text-slate-500 mt-1">Sombra</div>
            <ColorField label="Cor da sombra" value={b.shadow_color || "#FF1A8C"} onChange={(v) => set("shadow_color", v)} testid="champion-shadow-color" />
            <div>
              <Label className="text-xs text-slate-600">Intensidade da sombra ({b.shadow_opacity ?? 60}%)</Label>
              <input
                type="range" min={0} max={100} step={1}
                value={b.shadow_opacity ?? 60}
                onChange={(e) => set("shadow_opacity", Number(e.target.value))}
                className="w-full mt-1"
                data-testid="champion-shadow-opacity"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Desfoque ({shBlur}px)</Label>
              <input
                type="range" min={0} max={80} step={1}
                value={shBlur}
                onChange={(e) => set("shadow_blur", Number(e.target.value))}
                className="w-full mt-1"
                data-testid="champion-shadow-blur"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Offset X ({shX}px)</Label>
              <input
                type="range" min={-30} max={30} step={1}
                value={shX}
                onChange={(e) => set("shadow_x", Number(e.target.value))}
                className="w-full mt-1"
                data-testid="champion-shadow-x"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-slate-600">Offset Y ({shY}px)</Label>
              <input
                type="range" min={-30} max={30} step={1}
                value={shY}
                onChange={(e) => set("shadow_y", Number(e.target.value))}
                className="w-full mt-1"
                data-testid="champion-shadow-y"
              />
            </div>
          </div>

          {/* Inner shadow — sombra interior dentro da estrela */}
          <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100">
            <div className="col-span-2 text-[11px] font-bold uppercase tracking-wide text-slate-500 mt-1">Sombra interior</div>
            <ColorField label="Cor da sombra interior" value={b.inner_shadow_color || "#000000"} onChange={(v) => set("inner_shadow_color", v)} testid="champion-inner-color" />
            <div>
              <Label className="text-xs text-slate-600">Intensidade ({b.inner_shadow_opacity ?? 0}%)</Label>
              <input
                type="range" min={0} max={100} step={1}
                value={b.inner_shadow_opacity ?? 0}
                onChange={(e) => set("inner_shadow_opacity", Number(e.target.value))}
                className="w-full mt-1"
                data-testid="champion-inner-opacity"
              />
              <p className="text-[10px] text-slate-400">0 = desligada</p>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Desfoque ({b.inner_shadow_blur ?? 2})</Label>
              <input
                type="range" min={0} max={10} step={1}
                value={b.inner_shadow_blur ?? 2}
                onChange={(e) => set("inner_shadow_blur", Number(e.target.value))}
                className="w-full mt-1"
                data-testid="champion-inner-blur"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Offset X ({b.inner_shadow_x ?? 0})</Label>
              <input
                type="range" min={-10} max={10} step={1}
                value={b.inner_shadow_x ?? 0}
                onChange={(e) => set("inner_shadow_x", Number(e.target.value))}
                className="w-full mt-1"
                data-testid="champion-inner-x"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Offset Y ({b.inner_shadow_y ?? 2})</Label>
              <input
                type="range" min={-10} max={10} step={1}
                value={b.inner_shadow_y ?? 2}
                onChange={(e) => set("inner_shadow_y", Number(e.target.value))}
                className="w-full mt-1"
                data-testid="champion-inner-y"
              />
            </div>
          </div>

          {/* Text formatting — per-line size, alignment, weight + icon + padding */}
          <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100">
            <div className="col-span-2 text-[11px] font-bold uppercase tracking-wide text-slate-500 mt-1">Ícone + formatação do texto</div>

            {/* Icon picker — grid of clickable icons */}
            <div className="col-span-2">
              <Label className="text-xs text-slate-600 mb-1.5 block">Ícone (escolhe um)</Label>
              <div className="flex flex-wrap gap-1.5" data-testid="champion-icon-picker">
                {Object.entries(BADGE_ICONS).map(([name, IconCmp]) => {
                  const active = (b.icon || "Trophy") === name;
                  return (
                    <button
                      key={name}
                      type="button"
                      onClick={() => set("icon", name)}
                      title={name}
                      className={`w-9 h-9 rounded-lg border flex items-center justify-center transition ${active ? "bg-pink-500 border-pink-600 shadow-md" : "bg-white border-slate-200 hover:bg-slate-50"}`}
                      data-testid={`champion-icon-${name}`}
                    >
                      <IconCmp className="w-4 h-4" style={{ color: active ? "#FFD93D" : (b.icon_color || "#FFD93D") }} />
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="text-xs text-slate-600">Tamanho do ícone ({b.icon_size ?? 22}%)</Label>
              <input
                type="range" min={0} max={40} step={1}
                value={b.icon_size ?? 22}
                onChange={(e) => set("icon_size", Number(e.target.value))}
                className="w-full mt-1"
                data-testid="champion-icon-size"
              />
              <p className="text-[10px] text-slate-400">0 = sem ícone</p>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Margem interior ({b.text_padding ?? 16}%)</Label>
              <input
                type="range" min={0} max={20} step={1}
                value={b.text_padding ?? 16}
                onChange={(e) => set("text_padding", Number(e.target.value))}
                className="w-full mt-1"
                data-testid="champion-padding"
              />
              <p className="text-[10px] text-slate-400">Espaço entre texto e bicos</p>
            </div>

            {/* Icon position anchor (X/Y inside the badge) */}
            <div className="col-span-2 text-[10px] font-bold uppercase tracking-wide text-slate-500 mt-2">Posição do ícone dentro do crachá</div>
            <div>
              <Label className="text-xs text-slate-600">Ícone Y ({b.icon_top_pct ?? 18}%)</Label>
              <input
                type="range" min={0} max={100} step={1}
                value={b.icon_top_pct ?? 18}
                onChange={(e) => set("icon_top_pct", Number(e.target.value))}
                className="w-full mt-1"
                data-testid="champion-icon-top"
              />
              <p className="text-[10px] text-slate-400">0 = topo · 100 = base</p>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Ícone X ({b.icon_left_pct ?? 50}%)</Label>
              <input
                type="range" min={0} max={100} step={1}
                value={b.icon_left_pct ?? 50}
                onChange={(e) => set("icon_left_pct", Number(e.target.value))}
                className="w-full mt-1"
                data-testid="champion-icon-left"
              />
              <p className="text-[10px] text-slate-400">50 = centro</p>
            </div>

            {/* Text block position (whole stack) */}
            <div className="col-span-2 text-[10px] font-bold uppercase tracking-wide text-slate-500 mt-2">Posição do bloco de texto</div>
            <div>
              <Label className="text-xs text-slate-600">Texto Y ({b.text_top_pct ?? 50}%)</Label>
              <input
                type="range" min={0} max={100} step={1}
                value={b.text_top_pct ?? 50}
                onChange={(e) => set("text_top_pct", Number(e.target.value))}
                className="w-full mt-1"
                data-testid="champion-text-top"
              />
              <p className="text-[10px] text-slate-400">50 = centro · aumenta para empurrar texto para baixo (evita sobrepor o ícone)</p>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Texto X ({b.text_left_pct ?? 50}%)</Label>
              <input
                type="range" min={0} max={100} step={1}
                value={b.text_left_pct ?? 50}
                onChange={(e) => set("text_left_pct", Number(e.target.value))}
                className="w-full mt-1"
                data-testid="champion-text-left"
              />
              <p className="text-[10px] text-slate-400">50 = centro</p>
            </div>

            {[
              { n: 1, defaultSize: 8,  defaultAlign: "center", defaultWeight: 900, defaultGap: 0 },
              { n: 2, defaultSize: 9,  defaultAlign: "center", defaultWeight: 900, defaultGap: 0.5 },
              { n: 3, defaultSize: 11, defaultAlign: "center", defaultWeight: 900, defaultGap: 1.5 },
            ].map(({ n, defaultSize, defaultAlign, defaultWeight, defaultGap }) => (
              <div key={n} className="col-span-2 bg-slate-50 rounded-lg p-2 grid grid-cols-3 gap-2">
                <div className="col-span-3 text-[10px] font-bold text-slate-600">Linha {n}</div>
                <div>
                  <Label className="text-[10px] text-slate-500">Tamanho ({b[`line${n}_size`] ?? defaultSize}%)</Label>
                  <input
                    type="range" min={4} max={20} step={1}
                    value={b[`line${n}_size`] ?? defaultSize}
                    onChange={(e) => set(`line${n}_size`, Number(e.target.value))}
                    className="w-full"
                    data-testid={`champion-l${n}-size`}
                  />
                </div>
                <div>
                  <Label className="text-[10px] text-slate-500">Alinhamento</Label>
                  <select
                    value={b[`line${n}_align`] || defaultAlign}
                    onChange={(e) => set(`line${n}_align`, e.target.value)}
                    className="w-full h-7 rounded border border-slate-200 bg-white px-1 text-xs"
                    data-testid={`champion-l${n}-align`}
                  >
                    <option value="left">Esquerda</option>
                    <option value="center">Centro</option>
                    <option value="right">Direita</option>
                  </select>
                </div>
                <div>
                  <Label className="text-[10px] text-slate-500">Peso</Label>
                  <select
                    value={b[`line${n}_weight`] || defaultWeight}
                    onChange={(e) => set(`line${n}_weight`, Number(e.target.value))}
                    className="w-full h-7 rounded border border-slate-200 bg-white px-1 text-xs"
                    data-testid={`champion-l${n}-weight`}
                  >
                    <option value={400}>Regular</option>
                    <option value={500}>Médio</option>
                    <option value={600}>Semi-bold</option>
                    <option value={700}>Bold</option>
                    <option value={800}>Extra-bold</option>
                    <option value={900}>Black</option>
                  </select>
                </div>
                <div>
                  <Label className="text-[10px] text-slate-500">Espaço acima ({(b[`line${n}_gap`] ?? defaultGap)}%)</Label>
                  <input
                    type="range" min={-10} max={20} step={0.5}
                    value={b[`line${n}_gap`] ?? defaultGap}
                    onChange={(e) => set(`line${n}_gap`, Number(e.target.value))}
                    className="w-full"
                    data-testid={`champion-l${n}-gap`}
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-[10px] text-slate-500">Deslocamento X ({b[`line${n}_offset_x`] ?? 0}%)</Label>
                  <input
                    type="range" min={-50} max={50} step={1}
                    value={b[`line${n}_offset_x`] ?? 0}
                    onChange={(e) => set(`line${n}_offset_x`, Number(e.target.value))}
                    className="w-full"
                    data-testid={`champion-l${n}-offset-x`}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Position + rotation */}
          <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100">
            <div className="col-span-2 text-[11px] font-bold uppercase tracking-wide text-slate-500 mt-1">Posição na página</div>
            <div>
              <Label className="text-xs text-slate-600">Posição X ({b.position_x ?? 92}%)</Label>
              <input
                type="range" min={0} max={100}
                value={b.position_x ?? 92}
                onChange={(e) => set("position_x", Number(e.target.value))}
                className="w-full mt-1"
                data-testid="champion-pos-x"
              />
              <p className="text-[10px] text-slate-400">0 = esquerda · 50 = centro · 100 = direita</p>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Posição Y ({b.position_y ?? 12}%)</Label>
              <input
                type="range" min={0} max={100}
                value={b.position_y ?? 12}
                onChange={(e) => set("position_y", Number(e.target.value))}
                className="w-full mt-1"
                data-testid="champion-pos-y"
              />
              <p className="text-[10px] text-slate-400">0 = topo · 50 = meio · 100 = fundo</p>
            </div>
            {/* Quick preset anchors (3x3 grid) */}
            <div className="col-span-2">
              <Label className="text-xs text-slate-600 mb-1 block">Atalhos de posição</Label>
              <div className="grid grid-cols-3 gap-1 w-32" data-testid="champion-pos-presets">
                {[
                  [8,8],   [50,8],  [92,8],
                  [8,50],  [50,50], [92,50],
                  [8,92],  [50,92], [92,92],
                ].map(([x, y], idx) => {
                  const active = (b.position_x ?? 92) === x && (b.position_y ?? 12) === y;
                  return (
                    <button
                      key={`pos-${x}-${y}`}
                      type="button"
                      onClick={() => { set("position_x", x); set("position_y", y); }}
                      title={`X=${x}% Y=${y}%`}
                      className={`h-7 rounded border ${active ? "bg-pink-500 border-pink-600" : "bg-white border-slate-200 hover:bg-slate-50"}`}
                      data-testid={`champion-pos-preset-${idx}`}
                    />
                  );
                })}
              </div>
            </div>
            <div className="col-span-2 text-[11px] font-bold uppercase tracking-wide text-slate-500 mt-2">Rotação</div>
            <div>
              <Label className="text-xs text-slate-600">Rotação do crachá ({b.rotation_deg ?? -10}°)</Label>
              <input
                type="range" min={-30} max={30}
                value={b.rotation_deg ?? -10}
                onChange={(e) => set("rotation_deg", Number(e.target.value))}
                className="w-full mt-1"
                data-testid="champion-rotation"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Rotação do ícone ({b.icon_rotation ?? 0}°)</Label>
              <input
                type="range" min={-180} max={180} step={5}
                value={b.icon_rotation ?? 0}
                onChange={(e) => set("icon_rotation", Number(e.target.value))}
                className="w-full mt-1"
                data-testid="champion-icon-rotation"
              />
              <p className="text-[10px] text-slate-400">roda apenas o ícone dentro do crachá</p>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Rotação Linha 1 ({b.line1_rotation ?? -6}°)</Label>
              <input
                type="range" min={-25} max={25}
                value={b.line1_rotation ?? -6}
                onChange={(e) => set("line1_rotation", Number(e.target.value))}
                className="w-full mt-1"
                data-testid="champion-r1"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Rotação Linha 2 ({b.line2_rotation ?? -2}°)</Label>
              <input
                type="range" min={-25} max={25}
                value={b.line2_rotation ?? -2}
                onChange={(e) => set("line2_rotation", Number(e.target.value))}
                className="w-full mt-1"
                data-testid="champion-r2"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Rotação Linha 3 ({b.line3_rotation ?? 6}°)</Label>
              <input
                type="range" min={-25} max={25}
                value={b.line3_rotation ?? 6}
                onChange={(e) => set("line3_rotation", Number(e.target.value))}
                className="w-full mt-1"
                data-testid="champion-r3"
              />
            </div>
          </div>
        </div>

        {/* ── Right: live preview ── */}
        <div className="bg-slate-900 rounded-xl flex items-center justify-center p-2 md:sticky md:top-20" data-testid="champion-preview" style={{ alignSelf: "start" }}>
          <style>{`
            @keyframes cbp-shimmer { 0%{transform:rotate(0)} 100%{transform:rotate(360deg)} }
            @keyframes cbp-shimmer-sweep { 0%{transform:translateX(-140%) skewX(-18deg)} 100%{transform:translateX(140%) skewX(-18deg)} }
            @keyframes cbp-shimmer-radial { 0%,100%{transform:scale(0.6);opacity:0.05} 50%{transform:scale(1.15);opacity:0.6} }
            @keyframes cbp-shimmer-holo { 0%{filter:hue-rotate(0deg) saturate(1.15)} 100%{filter:hue-rotate(360deg) saturate(1.15)} }
            @keyframes cbp-shimmer-twinkle { 0%,100%{opacity:0.15} 25%{opacity:0.55} 50%{opacity:0.85} 75%{opacity:0.4} }
            @keyframes cbp-shimmer-lightning { 0%,88%,100%{opacity:0} 90%,94%{opacity:0.75} 92%{opacity:0.15} }
          `}</style>
          {enabled ? (
            <div className="relative select-none" style={{ width: previewSize, height: previewSize, transform: `rotate(${rot}deg)` }}>
              <svg viewBox="0 0 100 100" className="w-full h-full" style={{ filter: shadowCss, overflow: "visible" }}>
                {innerOn && (
                  <defs>
                    <filter id={innerId} x="-20%" y="-20%" width="140%" height="140%">
                      <feGaussianBlur in="SourceAlpha" stdDeviation={innerBlur} />
                      <feOffset dx={innerX} dy={innerY} result="offset-blur" />
                      <feComposite operator="out" in="SourceAlpha" in2="offset-blur" result="inverse" />
                      <feFlood floodColor={innerColor} floodOpacity={innerAlpha} result="color" />
                      <feComposite operator="in" in="color" in2="inverse" result="shadow" />
                      <feComposite operator="over" in="shadow" in2="SourceGraphic" />
                    </filter>
                  </defs>
                )}
                {borderWidth > 0 && (
                  <path
                    d={path}
                    fill="none"
                    stroke={borderColor}
                    strokeWidth={borderWidth}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    vectorEffect="non-scaling-stroke"
                  />
                )}
                <path
                  d={path}
                  fill={fillColor}
                  stroke={cornerRound > 0 ? fillColor : "none"}
                  strokeWidth={cornerRound}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  filter={innerOn ? `url(#${innerId})` : undefined}
                />
                {/* ─── Shimmer effect preview overlays ─── */}
                {b.effect === "shimmer" && (
                  <foreignObject x="0" y="0" width="100" height="100" style={{ clipPath: `path('${path}')` }}>
                    <div style={{ width: "100%", height: "100%", background: "conic-gradient(from 0deg, transparent 0deg, rgba(255,255,255,0.28) 50deg, transparent 110deg, transparent 360deg)", mixBlendMode: "screen", animation: "cbp-shimmer 4.5s linear infinite", transformOrigin: "50% 50%" }} />
                  </foreignObject>
                )}
                {b.effect === "shimmer-sweep" && (
                  <foreignObject x="0" y="0" width="100" height="100" style={{ clipPath: `path('${path}')` }}>
                    <div style={{ position: "relative", width: "100%", height: "100%", overflow: "hidden" }}>
                      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(90deg, transparent 30%, rgba(255,255,255,0.55) 50%, transparent 70%)", mixBlendMode: "screen", animation: "cbp-shimmer-sweep 3.2s ease-in-out infinite" }} />
                    </div>
                  </foreignObject>
                )}
                {b.effect === "shimmer-radial" && (
                  <foreignObject x="0" y="0" width="100" height="100" style={{ clipPath: `path('${path}')` }}>
                    <div style={{ width: "100%", height: "100%", background: "radial-gradient(circle at 50% 50%, rgba(255,255,255,0.75) 0%, rgba(255,255,255,0.1) 45%, transparent 70%)", mixBlendMode: "screen", animation: "cbp-shimmer-radial 2.4s ease-in-out infinite", transformOrigin: "50% 50%" }} />
                  </foreignObject>
                )}
                {b.effect === "shimmer-holo" && (
                  <foreignObject x="0" y="0" width="100" height="100" style={{ clipPath: `path('${path}')` }}>
                    <div style={{ width: "100%", height: "100%", background: "linear-gradient(120deg, rgba(255,0,180,0.45), rgba(0,200,255,0.45), rgba(255,220,0,0.45), rgba(0,255,120,0.45), rgba(255,0,180,0.45))", backgroundSize: "300% 300%", mixBlendMode: "overlay", animation: "cbp-shimmer-holo 6s linear infinite" }} />
                  </foreignObject>
                )}
                {b.effect === "shimmer-twinkle" && (
                  <foreignObject x="0" y="0" width="100" height="100" style={{ clipPath: `path('${path}')` }}>
                    <div style={{ position: "relative", width: "100%", height: "100%" }}>
                      {[
                        { top: "20%", left: "25%", size: 8,  delay: "0s"   },
                        { top: "35%", left: "70%", size: 6,  delay: "0.4s" },
                        { top: "55%", left: "18%", size: 9,  delay: "0.9s" },
                        { top: "62%", left: "78%", size: 7,  delay: "1.3s" },
                        { top: "78%", left: "40%", size: 8,  delay: "1.7s" },
                        { top: "28%", left: "50%", size: 5,  delay: "2.1s" },
                      ].map((s) => (
                        <div key={`cbp-tw-${s.top}-${s.left}`} style={{ position: "absolute", top: s.top, left: s.left, width: s.size, height: s.size, borderRadius: "50%", background: "radial-gradient(circle, #FFFFFF 0%, rgba(255,255,255,0) 70%)", animation: `cbp-shimmer-twinkle 2.4s ease-in-out ${s.delay} infinite`, mixBlendMode: "screen" }} />
                      ))}
                    </div>
                  </foreignObject>
                )}
                {b.effect === "shimmer-lightning" && (
                  <foreignObject x="0" y="0" width="100" height="100" style={{ clipPath: `path('${path}')` }}>
                    <div style={{ width: "100%", height: "100%", background: "linear-gradient(60deg, transparent 40%, rgba(255,255,255,0.75) 50%, transparent 60%)", mixBlendMode: "screen", animation: "cbp-shimmer-lightning 3.6s ease-out infinite" }} />
                  </foreignObject>
                )}
              </svg>
              {/* Icon anchor — matches Home.jsx position controls */}
              {(b.icon_size ?? 22) > 0 && (() => {
                const PreviewIcon = BADGE_ICONS[b.icon] || Trophy;
                return (
                  <div
                    className="absolute pointer-events-none"
                    style={{
                      top: `${b.icon_top_pct ?? 18}%`,
                      left: `${b.icon_left_pct ?? 50}%`,
                      transform: "translate(-50%, 0)",
                    }}
                  >
                    <PreviewIcon
                      style={{
                        color: b.icon_color || "#FFD93D",
                        width: previewSize * ((b.icon_size ?? 22) / 100),
                        height: previewSize * ((b.icon_size ?? 22) / 100),
                        transform: `rotate(${b.icon_rotation ?? 0}deg)`,
                        transformOrigin: "center",
                      }}
                    />
                  </div>
                );
              })()}
              {/* Text stack anchor — matches Home.jsx */}
              <div
                className="absolute"
                style={{
                  top: `${b.text_top_pct ?? 50}%`,
                  left: `${b.text_left_pct ?? 50}%`,
                  transform: "translate(-50%, -50%)",
                  width: `calc(${rIn * 2}% - ${(b.text_padding ?? 16) * 2}%)`,
                }}
              >
                <div
                  className="flex flex-col items-center justify-center uppercase leading-[1.05] text-center w-full"
                  style={{
                    fontFamily: `"${fontFam}", system-ui, sans-serif`,
                  }}
                >
                  <div
                    style={{
                      color: b.text_color || "#FFFFFF",
                      fontSize: previewSize * ((b.line1_size ?? 8) / 100),
                      fontWeight: b.line1_weight ?? 900,
                      textAlign: b.line1_align || "center",
                      transform: `translateX(${b.line1_offset_x ?? 0}%) rotate(${r1}deg)`,
                      transformOrigin: "center",
                      marginTop: previewSize * ((b.line1_gap ?? 0) / 100),
                      display: "inline-block",
                    }}
                  >{b.line1_pt || "TORNA-TE"}</div>
                  <div
                    style={{
                      color: b.text_color || "#FFFFFF",
                      fontSize: previewSize * ((b.line2_size ?? 9) / 100),
                      fontWeight: b.line2_weight ?? 900,
                      textAlign: b.line2_align || "center",
                      transform: `translateX(${b.line2_offset_x ?? 0}%) rotate(${r2}deg)`,
                      transformOrigin: "center",
                      marginTop: previewSize * ((b.line2_gap ?? 0.5) / 100),
                      display: "inline-block",
                    }}
                  >{b.line2_pt || "O CAMPEÃO"}</div>
                  <div
                    style={{
                      color: b.icon_color || "#FFD93D",
                      fontSize: previewSize * ((b.line3_size ?? 11) / 100),
                      fontWeight: b.line3_weight ?? 900,
                      lineHeight: 1,
                      textAlign: b.line3_align || "center",
                      transform: `translateX(${b.line3_offset_x ?? 0}%) rotate(${r3}deg)`,
                      transformOrigin: "center",
                      marginTop: previewSize * ((b.line3_gap ?? 1.5) / 100),
                      display: "inline-block",
                    }}
                  >
                    {b.line3_pt || "SUPERTMATIK!"}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-slate-400 text-sm">Crachá escondido</div>
          )}
        </div>
      </div>
    </div>
  );
}


